import React, { useState } from 'react';
import { Alert } from '@trellixio/roaster-coffee';
import { toast } from 'sonner';
import { useMutation, useQueryClient, useIsMutating } from '@tanstack/react-query';
import Modal from 'components/shared/Modal';
import { useGetAgency, useAPI } from 'hooks';
import Locales from 'locales';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from 'components/ui/card';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from 'components/ui/select';
import { Item, ItemActions, ItemContent, ItemDescription, ItemTitle } from 'components/ui/item';
import PropTypes from 'prop-types';
import AuthorizationAlert from './AuthorizationAlert';

const CONST_MERGED = 'merged';
const CONST_REQUESTING = 'requesting';

const MergerBlock = ({ currentAgency = null, sortedAgencyList = null, combinedAgenciesArray = null }) => {
  const { api } = useAPI();
  const [openMergerModal, setOpenMergerModal] = useState(false);
  const [openCancelMergerModal, setOpenCancelMergerModal] = useState(false);
  const queryClient = useQueryClient();
  const [selectedAgency, setSelectedAgency] = useState(
    sortedAgencyList && sortedAgencyList.length > 0 ? sortedAgencyList[0].id : ''
  );
  const { mergerRequested, mergerApproved, agencyMasterName, agencyId, agencyMerger, agencyMasterId } =
    useGetAgency(currentAgency);
  const isMutating = useIsMutating() > 0;
  const showAlertWithCancelButton = !mergerApproved && mergerRequested;
  const showSelectAgencyField = mergerApproved || mergerRequested || (!mergerApproved && mergerRequested);

  const { mutate: mergerRequest } = useMutation({
    mutationFn: (selectedAgencyId) =>
      api.post(`agency/current/merger_request/?agency_master_id=${selectedAgencyId}`, {
        merger: {
          approved: null,
          requested: null,
          rejected: null,
          master_id: agencyId,
          master_name: null,
          trip_count: null,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agency/'] });
      queryClient.invalidateQueries({ queryKey: ['agency/', 'is_sa'] });
      toast.success(Locales.authorization.toast.merger_request);
    },
  });

  const { mutate: cancelMergerRequest } = useMutation({
    mutationFn: () => api.post(`agency/current/merger_cancel/`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agency/'] });
      queryClient.invalidateQueries({ queryKey: ['agency/', 'is_sa'] });
      toast.success(Locales.authorization.toast.merger_cancel);
    },
  });

  const { mutate: approveMergerRequest } = useMutation({
    mutationFn: (agencySlaveId) =>
      api.post(`agency/current/merger_approve/?agency_slave_id=${agencySlaveId}`, {
        merger: {
          approved: agencyMerger.approved,
          requested: agencyMerger.requested,
          rejected: null,
          master_id: agencyMasterId,
          master_name: null,
          trip_count: null,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agency/'] });
      queryClient.invalidateQueries({ queryKey: ['agency/stats/default/'] });
      queryClient.invalidateQueries({ queryKey: ['agency/', 'is_sa'] });
      toast.success(Locales.authorization.toast.merger_approve);
    },
  });

  const handleOpenMergerRequestModal = (e) => {
    e.preventDefault();
    setOpenMergerModal(true);
  };
  const handleOpenCancelMergerRequestModal = (e) => {
    e.preventDefault();
    setOpenCancelMergerModal(true);
  };

  const getMessage = (agency) => {
    if (agency.tripCount === null || agency.tripCount === 0) {
      return agency.type === CONST_REQUESTING
        ? Locales.authorization.trip_merge_wanted
        : Locales.authorization.trip_merged;
    }

    return agency.type === CONST_MERGED
      ? Locales.authorization.trip_transferred
      : Locales.authorization.trip_transfer_wanted;
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>
            <span className="text-xl">{Locales.authorization.grouping}</span>
          </CardTitle>

          {mergerRequested && (
            <CardDescription>
              <span className="text-gray-600">
                {mergerApproved
                  ? Locales.authorization.alert.merger_approved
                  : Locales.authorization.alert.merger_pending}
              </span>
            </CardDescription>
          )}
        </CardHeader>
        {!mergerRequested && (
          <CardContent>
            <div className="space-y-2 mb-4">
              <p>{Locales.authorization.grouping_explanation}</p>

              {!showAlertWithCancelButton && <p>{Locales.authorization.grouping_initialization}</p>}
            </div>

            <form>
              {showAlertWithCancelButton && (
                <>
                  <div className="mb-4">
                    <Alert
                      level="info"
                      message={
                        <span className="alert-children">
                          {Locales.formatString(Locales.authorization.alert.information_message, {
                            name: <strong>{agencyMasterName}</strong>,
                            pilgrim_add_not_allowed: (
                              <strong>{Locales.authorization.alert.pilgrim_add_not_allowed}</strong>
                            ),
                          })}
                        </span>
                      }
                    />
                  </div>
                </>
              )}

              {!showSelectAgencyField && (
                <Select value={selectedAgency} onValueChange={setSelectedAgency}>
                  <SelectTrigger>
                    <SelectValue placeholder={Locales.authorization.select_society} />
                  </SelectTrigger>

                  <SelectContent>
                    {sortedAgencyList.map((agency) => (
                      <SelectItem key={agency.id} value={agency.id}>
                        {agency.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              <AuthorizationAlert
                condition={mergerApproved && mergerRequested}
                level="info"
                message={Locales.formatString(Locales.authorization.alert.merger_accepted, {
                  name: <strong>{agencyMasterName}</strong>,
                })}
              />
            </form>
          </CardContent>
        )}

        {!mergerApproved && (
          <CardFooter>
            {showAlertWithCancelButton && (
              <Button
                variant="destructive"
                type="button"
                className="w-full"
                disabled={isMutating}
                onClick={(e) => handleOpenCancelMergerRequestModal(e)}
              >
                {Locales.authorization.cancel_grouping_request}
              </Button>
            )}

            {!showSelectAgencyField && (
              <Button
                variant="hajj"
                type="button"
                className="w-full"
                disabled={isMutating}
                onClick={(e) => handleOpenMergerRequestModal(e)}
              >
                {Locales.authorization.grouping_request}
              </Button>
            )}
          </CardFooter>
        )}
      </Card>

      <div className="space-y-2">
        {combinedAgenciesArray.map((agency) => (
          <Item key={agency.id} variant="outline" className="border-gray-300">
            <ItemContent>
              <ItemTitle>
                {Locales.authorization.group} - {agency.name}
              </ItemTitle>
              <ItemDescription>
                {Locales.formatString(getMessage(agency), {
                  name: <strong>{agency.name}</strong>,
                  trip_count: agency.tripCount,
                })}
              </ItemDescription>
            </ItemContent>
            {agency.type === CONST_REQUESTING && (
              <ItemActions>
                <Button
                  type="button"
                  variant="hajj"
                  disabled={isMutating}
                  onClick={() => approveMergerRequest(agency.id)}
                >
                  {Locales.authorization.button.accept}
                </Button>
              </ItemActions>
            )}
          </Item>
        ))}
      </div>

      {/* request merger modal */}
      <Modal
        isOpen={openMergerModal}
        onOpenChange={setOpenMergerModal}
        title={Locales.authorization.merger_request}
        footer={
          <>
            <Button variant="outline" onClick={() => setOpenMergerModal(false)}>
              {Locales.base.button.discard}
            </Button>
            <Button
              variant="hajj"
              disabled={isMutating}
              onClick={() => {
                mergerRequest(selectedAgency);
                setOpenMergerModal(false);
              }}
            >
              {Locales.authorization.request_the_merger}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p>{Locales.formatString(Locales.authorization.merger_request_message)}</p>
          <strong>
            <p>{Locales.formatString(Locales.authorization.pilgrim_add_not_allowed)}</p>
          </strong>
        </div>
      </Modal>

      {/* cancel merger request modal */}
      <Modal
        isOpen={openCancelMergerModal}
        onOpenChange={setOpenCancelMergerModal}
        title={Locales.authorization.merger_cancel}
        footer={
          <>
            <Button variant="outline" onClick={() => setOpenCancelMergerModal(false)}>
              {Locales.authorization.conserver}
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                cancelMergerRequest();
                setOpenCancelMergerModal(false);
              }}
            >
              {Locales.authorization.cancel_grouping_request}
            </Button>
          </>
        }
      >
        <p>{Locales.formatString(Locales.authorization.cancel_merger)}</p>
      </Modal>
    </>
  );
};

MergerBlock.propTypes = {
  currentAgency: PropTypes.shape({}),
  sortedAgencyList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
    })
  ),
  combinedAgenciesArray: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      tripCount: PropTypes.number,
      type: PropTypes.string,
    })
  ),
};

export default MergerBlock;
